import { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Check, Loader2, AlertCircle } from 'lucide-react';
import { useTurnstile, getTurnstileToken } from '../hooks/useTurnstile';

interface NewsletterFormProps {
  source: 'footer' | 'homepage_banner' | 'report_page' | 'post_submission';
  variant: 'inline' | 'compact';
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function NewsletterForm({ source, variant }: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [error, setError] = useState('');
  const [widgetId, setWidgetId] = useState<string | null>(null);

  const containerId = `turnstile-${source}-${Date.now()}`;
  const containerRef = useRef<string>(containerId);
  const { renderWidget, resetWidget, getSiteKey } = useTurnstile();

  const turnstileContainerId = `turnstile-container-${source}`;

  useEffect(() => {
    // Render Turnstile widget when component mounts
    const siteKey = getSiteKey();
    if (!siteKey) return;

    const timer = setTimeout(() => {
      const id = renderWidget(containerRef.current);
      if (id) setWidgetId(id);
    }, 200);

    return () => clearTimeout(timer);
  }, [renderWidget, getSiteKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatus('submitting');

    // Get Turnstile token
    const token = getTurnstileToken(containerRef.current);
    if (!token) {
      setError('Please complete the captcha verification');
      setStatus('error');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setStatus('error');
      return;
    }

    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/newsletter-subscribe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
          },
          body: JSON.stringify({
            email: email.toLowerCase().trim(),
            token,
            source,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to subscribe');
      }

      setStatus('success');
      setEmail('');

      // Reset Turnstile widget
      if (widgetId) {
        resetWidget(widgetId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to subscribe. Please try again.');
      setStatus('error');

      // Reset Turnstile widget on error
      if (widgetId) {
        resetWidget(widgetId);
      }
    }
  };

  if (status === 'success') {
    return (
      <div
        className="flex items-center gap-2 text-sage-400"
        role="status"
        aria-live="polite"
      >
        <Check className="w-4 h-4" />
        <span className="text-sm">You're subscribed!</span>
      </div>
    );
  }

  const isCompact = variant === 'compact';

  return (
    <form onSubmit={handleSubmit} className={isCompact ? 'flex items-center gap-2' : 'space-y-3'}>
      {/* Label for accessibility */}
      <label htmlFor={`newsletter-email-${source}`} className="sr-only">
        Email address for newsletter
      </label>

      {/* Input group */}
      <div className={isCompact ? 'flex-1 relative' : 'relative'}>
        {!isCompact && (
          <div className="flex items-center gap-1.5 mb-2">
            <Mail className="w-4 h-4 text-flag-500" />
            <span className="text-sm font-medium text-snow">
              {source === 'footer' && 'Get Scam Alerts'}
              {source === 'homepage_banner' && 'Stay Protected'}
              {source === 'report_page' && 'Get notified when new scams like this are reported'}
              {source === 'post_submission' && 'Want to receive alerts when new scams are flagged?'}
            </span>
          </div>
        )}

        <div className={isCompact ? 'flex items-center gap-2' : 'space-y-2'}>
          <div className={isCompact ? 'flex-1 relative' : 'relative'}>
            <input
              type="email"
              id={`newsletter-email-${source}`}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === 'error') setStatus('idle');
              }}
              placeholder={isCompact ? 'Your email address' : 'you@example.com'}
              className={`w-full bg-ink-700 border border-line rounded-lg text-sm text-snow placeholder-ash-600 focus:outline-none focus:ring-2 focus:ring-flag-500/50 ${
                isCompact ? 'px-3 py-2' : 'px-3 py-2.5'
              }`}
              required
              disabled={status === 'submitting'}
            />
            {isCompact && (
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ash-600 pointer-events-none" />
            )}
          </div>

          {/* Turnstile container */}
          <div
            id={containerRef.current}
            className="turnstile-container flex items-center justify-center min-w-[130px]"
          />

          {/* Submit button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className={`font-medium rounded-lg transition flex items-center justify-center gap-1.5 ${
              isCompact
                ? 'bg-flag-500 hover:bg-flag-600 text-white px-4 py-2 text-sm'
                : 'w-full bg-flag-500 hover:bg-flag-600 text-white py-2.5 text-sm'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isCompact ? '' : 'Subscribing...'}
              </>
            ) : (
              <>
                {isCompact ? 'Get Alerts →' : 'Subscribe'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Subtext */}
      {!isCompact && (
        <p className="text-xs text-ash-600">
          {source === 'footer' && 'No spam. Unsubscribe anytime.'}
          {source === 'homepage_banner' && 'Join thousands of Kenyans staying scam-aware.'}
          {source === 'report_page' && 'Join thousands of Kenyans staying scam-aware.'}
          {source === 'post_submission' && 'Thanks for helping the community stay safe.'}
        </p>
      )}

      {/* Error message */}
      {status === 'error' && (
        <div
          className="flex items-center gap-2 text-flag-500 text-sm"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}
